import React from 'react'
import type { Contact, Deal } from '@paintscout/api'
import type { Filter } from 'json-to-lucene'
import type { DialogProps } from '@paintscout/ui/components/Dialog'

export type OpenDialogProps<T> = Omit<T, 'open' | 'ref'> & {
  'data-testid'?: string
}

export interface CrmDialogContext {
  subtitle?: string
  company?: {
    id?: string
    name?: string
    hide?: boolean
  }
  contact?: {
    id?: string
    name?: string
    hide?: boolean
    search?: {
      doWildcardQueryOnMount?: boolean // boolean rn but could pretty easily be a string for a custom query in the future
      autoSelectSingleResult?: boolean
      filters?: Filter[]
    }
    fullContact?: Contact
  }
  deal?: {
    id?: string
    name?: string
    hide?: boolean
    search?: {
      query?: string // lucene query for deals
      postFilter?: (deal: Deal) => boolean
    }
  }
}

export interface DialogStackContextValue {
  stack: Array<{
    component: React.ComponentType<DialogProps | any>
    props: any
  }>
  renderPortal: boolean

  setRenderPortal?: (ref: any) => any

  /**
   * Opens a dialog using the provided component and props
   */
  openDialog?: <T>(
    component: React.ComponentType<T>,
    props?: OpenDialogProps<T>,
    options?: { replace?: boolean; skip?: boolean }
  ) => any

  /**
   * Exposed in useCrmDialogs hook only
   */
  crmContext?: CrmDialogContext
  setCrmContext?: (ctx: CrmDialogContext) => void

  /**
   * Dismisses the active dialog
   */
  dismissDialog?: () => void
  dismissDialogs?: (amount?: number) => void
  dismissAllDialogs?: () => void
}

export default React.createContext<DialogStackContextValue>({
  stack: [],
  renderPortal: false,
  setRenderPortal: () => {},
  openDialog: () => {},
  dismissAllDialogs: () => {},
  dismissDialog: () => {}
})
